import styled, { keyframes } from 'styled-components';
import { minWidth } from 'config/constants';

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const PANEL_BREAKPOINT = 1540;

export const StyledDoughnutKpiCardLeft = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  justify-content: flex-start;

  .doughnut-kpi-card__chart-wrapper {
    position: relative;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    aspect-ratio: 1 / 1;
    max-width: 318px;
    min-height: 0;
    flex-shrink: 1;

    &--skeleton {
      border-radius: 50%;
      background: linear-gradient(90deg, #f2f2f2 25%, #e8e8e8 50%, #f2f2f2 75%);
      background-size: 200% 100%;
      animation: ${shimmer} 1.5s infinite;
      max-height: 260px;
      max-width: 260px !important;
    }
  }

  .doughnut-kpi-card__chart-center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${(props): string => props.theme.kcc.spacing.XS};
    pointer-events: none;
  }

  .doughnut-kpi-card__chart-percent {
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.HEADING_3}px`};
    font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BLACK};
    line-height: ${(props): number => props.theme.kcc.typography.LINE_HEIGHT.HEADING};
    margin: 0;
    color: ${(props): string => props.theme.kcc.colors.TEXT};

    ${minWidth.s} {
      font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.HEADING_2}px`};
    }

    ${minWidth.m} {
      font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.HEADING_1}px`};
    }
  }

  .doughnut-kpi-card__chart-label {
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.BASE}px`};
    font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    color: ${(props): string => props.theme.kcc.colors.TEXT};
    margin: 0;

    ${minWidth.m} {
      font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.LG}px`};
    }
  }

  .doughnut-kpi-card__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    margin-top: ${(props): string => props.theme.kcc.spacing.BASE};
    flex-shrink: 0;

    &--skeleton .ant-skeleton-input {
      height: 14px !important;
      width: 60px !important;
      min-width: unset !important;
      border-radius: 4px;
    }
  }

  .doughnut-kpi-card__legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.SM}px`};
    color: ${(props): string => props.theme.kcc.colors.TEXT_SECONDARY};
  }

  .doughnut-kpi-card__legend-dot {
    margin-top: 1px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .doughnut-kpi-card__legend-text {
    text-transform: capitalize;
  }

  .doughnut-kpi-card__alert {
    margin-top: auto;
    padding-top: ${(props): string => props.theme.kcc.spacing.BASE};
    flex-shrink: 0;
  }

  .doughnut-kpi-card__alert-count {
    display: flex;
    align-items: center;
    gap: ${(props): string => props.theme.kcc.spacing.XS};
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.BASE}px`};
    font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    margin-bottom: 0;
  }

  .doughnut-kpi-card__alert-count-number {
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.BASE}px`};

    ${minWidth.m} {
      font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.LG}px`};
    }
  }

  .doughnut-kpi-card__alert-count-label {
    text-transform: lowercase;
  }

  .doughnut-kpi-card__alert-text {
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.SM}px`};
    color: ${(props): string => props.theme.kcc.colors.TEXT_SECONDARY};
    margin: 6px 0 2px 0;
  }

  .doughnut-kpi-card__alert-link {
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.SM}px`};
    color: ${(props): string => props.theme.kcc.colors.TEXT};
    cursor: pointer;
    text-decoration: underline;
    background: none;
    border: none;
    padding: 0;
    display: none;

    ${minWidth.m} {
      display: inline;
    }

    ${minWidth.l} {
      display: none;
    }

    ${minWidth.custom(PANEL_BREAKPOINT)} {
      display: inline;
    }

    &:hover,
    &:active,
    &:visited,
    &:focus {
      color: ${(props): string => props.theme.kcc.colors.TEXT};
    }
  }

  .doughnut-kpi-card__modal-trigger {
    padding: 0;
    width: 20px;
    height: 20px;
    min-width: 0;
    display: inline-flex;

    &:hover,
    &:active,
    &:focus {
      color: ${(props): string => props.theme.kcc.colors.TEXT};
    }

    .ant-btn-icon {
      display: flex;

      .svg-icon {
        width: 20px;
        height: 20px;
      }
    }

    ${minWidth.m} {
      display: none;
    }

    ${minWidth.l} {
      display: inline-flex;
    }

    ${minWidth.custom(PANEL_BREAKPOINT)} {
      display: none;
    }
  }
`;

export const StyledDoughnutKpiCardRight = styled.div`
  display: contents;

  .doughnut-kpi-card__hint {
    margin-top: auto;
    padding-top: ${(props): string => props.theme.kcc.spacing.SM};
  }

  .doughnut-kpi-card__hint-body {
    display: block;
    padding: 8px 12px;
    background-color: ${(props): string => props.theme.kcc.colors.PETROL[100]};
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.SM}px`};
    color: ${(props): string => props.theme.kcc.colors.TEXT};
    line-height: ${(props): number => props.theme.kcc.typography.LINE_HEIGHT.BASE};
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 calc(50% + 8px), 8px 50%, 0 calc(50% - 8px));
  }
`;
